import { Button, Tooltip } from '@heroui/react'
import { useMutation } from '@tanstack/react-query'
import { BlandWebClient } from 'bland-client-js-sdk'
import config from 'config'
import { useAuth } from 'hooks/useAuth'
import { Mic, MicOff } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
// import { BlandWebClient } from './BlandClient'

export default function TalkToVoiceButton({ name, id }) {
  const { user } = useAuth()
  const [isCalling, setIsCalling] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const clientRef = useRef(null)

  const { mutateAsync: createClient } = useMutation({
    mutationFn: async () => $fetch('/v1/pathway/test_agent/init', {
      method: 'POST',
      body: {
        voice: id,
        prompt: `You are a helpful assistant. Have a natural conversation with the user. Be friendly and engaging. Keep responses concise and conversational.`,
      },
    }),
    onSuccess: async ({ data }) => {
      const client = new BlandWebClient(
        user?.user?.id,
        data.id,
        config.WS_URL,
      )

      client.on('conversationStarted', () => {
        setIsCalling(true)
        toast.success('Conversation started!')
      })

      client.on('conversationEnded', () => {
        setIsCalling(false)
        toast.info('Conversation ended')
      })

      client.on('error', (error) => {
        console.error('[SDK] Error:', error)
        setIsCalling(false)
        toast.error('Conversation error occurred')
      })

      clientRef.current = client
    },
    onError: (error) => {
      console.error('Error creating client:', error)
      toast.error('Error starting conversation, please try again.')
      setIsLoading(false)
    },
  })

  const stopConversation = async () => {
    if (clientRef.current) {
      try {
        await clientRef.current.stopConversation()
      }
      catch (error) {
        console.error('Error stopping conversation:', error)
      }
    }
  }

  const startConversation = async () => {
    if (clientRef.current) {
      try {
        await clientRef.current.initConversation({
          sampleRate: 44100,
        })
      }
      catch (error) {
        console.error('Error starting conversation:', error)
        toast.error('Error starting conversation')
        setIsLoading(false)
      }
    }
  }

  const initializeAndStart = async () => {
    setIsLoading(true)
    try {
      await createClient()
      // Wait a bit for client to be ready
      setTimeout(async () => {
        await startConversation()
        setIsLoading(false)
      }, 500)
    }
    catch (_error) {
      setIsLoading(false)
    }
  }

  const handleButtonClick = async () => {
    if (isCalling) {
      await stopConversation()
    }
    else {
      await initializeAndStart()
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return async () => {
      if (clientRef.current) {
        await clientRef.current.stopConversation()
        clientRef.current = null
      }
    }
  }, [])

  return (
    <Tooltip content="Click to start a conversation">
      <Button
        size="sm"
        variant="solid"
        radius="none"
        color={isCalling ? 'danger' : 'primary'}
        onPress={handleButtonClick}
        isLoading={isLoading}
        disableRipple
      >
        {
          isCalling
            ? (
                <>
                  <MicOff className="size-5" />
                  Stop
                </>
              )
            : (
                <>
                  <Mic className="size-4" />
                  Talk to
                  {' '}
                  {name}
                </>
              )
        }
      </Button>
    </Tooltip>
  )
}
